// AccountPanel — Phase 4 redesign (2026-05-11) + follow-up polish.
//
// Layout (single column on every viewport — the hub container is
// already constrained, so two columns wasn't pulling its weight):
//
//   1. Compact identity hero (brand-magenta gradient — Phase 8.1
//      swap from indigo, since the Profile section now wears
//      magenta after Home took the indigo identity)
//   2. **Account info** — read-only metadata. Placed FIRST under the
//      hero so the agent immediately confirms "this is my account".
//   3. **Personal details** — username, email, contact number (phone
//      column added by migration 062). One Save button lights up only
//      when a field is dirty.
//   4. **Your location** — DEFAULTS TO SUMMARY mode when a location is
//      already saved. Resolves state/district/area NAMES via the geo
//      endpoints (no need for the agent to look at IDs). The full
//      LocationCapture editor only appears when the agent taps Edit —
//      previously we were always showing the manual-pick/pincode UI
//      even when the location was complete.
//   5. **Security** — password change.
//   6. **Sign out** — opens a proper modal confirmation (no more
//      native confirm() popup), mirroring the player end's
//      LogoutButton pattern.
//
// Mutations:
//   - PUT  /admin/auth/me            { username, email, phone }
//   - POST /admin/auth/me/avatar      (multipart 'image')
//   - DELETE /admin/auth/me/avatar
//   - POST /admin/auth/change-password
//   - PATCH /admin/me-agent/location

import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { compressForUpload } from '@baaligames/shared-ui/lib/imageCompress';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import AvatarCropper from '@baaligames/shared-ui/components/AvatarCropper';
import Icon from '@baaligames/shared-ui/components/Icon';
import LocationCapture, { type GeoBag, isGeoLocated } from '@baaligames/shared-ui/components/LocationCapture';
import { INPUT_CLS } from '../../components/fieldStyles';

type ToastTone = 'ok' | 'err';
type Toast = { tone: ToastTone; msg: string } | null;

export default function AccountPanel() {
  const t = useT();
  const { admin, refresh, logout } = useAdminAuth();
  const nav = useNavigate();
  const [toast, setToast] = useState<Toast>(null);
  const [cropping, setCropping] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [signOutBusy, setSignOutBusy] = useState(false);

  if (!admin) return <p className="text-arena-600 dark:text-neon-200/80 text-sm">{t('agent.account.notSignedIn')}</p>;

  const me = admin as any;
  const isAgent = me.role === 'agent';

  const fieldLock = (field: 'username'|'email'|'avatar'|'password'): 'none'|'view'|'edit' => {
    if (me?.identity_locked) return 'view';
    const map = me?.identity_locked_fields;
    if (map && typeof map === 'object' && map[field]) return map[field];
    return 'edit';
  };

  const flash = (tone: ToastTone, msg: string) => {
    setToast({ tone, msg });
    setTimeout(() => setToast(null), 3000);
  };

  async function uploadCropped(rawFile: File) {
    setUploading(true);
    try {
      const file = await compressForUpload(rawFile, { preset: 'avatar' });
      const fd = new FormData();
      fd.append('image', file);
      await adminApi.postForm('/admin/auth/me/avatar', fd);
      await refresh();
      flash('ok', t('agent.account.toast.photoUpdated'));
    } catch (err) {
      flash('err', (err as ApiError).message || t('agent.account.error.uploadFailed'));
      throw err;
    } finally { setUploading(false); }
  }

  async function onRemoveAvatar() {
    if (!confirm(t('agent.account.avatar.confirmRemove'))) return;
    setUploading(true);
    try {
      await adminApi.del('/admin/auth/me/avatar');
      await refresh();
      flash('ok', t('agent.account.toast.photoRemoved'));
    } catch (err) { flash('err', (err as ApiError).message || t('agent.account.error.removeFailed')); }
    finally { setUploading(false); }
  }

  async function confirmSignOut() {
    setSignOutBusy(true);
    try {
      await logout();
      nav('/login', { replace: true });
    } catch {
      flash('err', t('agent.profile.signOut.error'));
      setSignOutBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            className={`rounded-2xl border px-4 py-3 text-[13px]
              ${toast.tone === 'ok'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-400/30 dark:text-emerald-200'
                : 'bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-500/10 dark:border-rose-400/30 dark:text-rose-200'}`}>
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Compact identity hero */}
      <HeroCard
        avatarUrl={me.avatar_url}
        username={me.username}
        email={me.email}
        isAgent={isAgent}
        canEditAvatar={fieldLock('avatar') === 'edit'}
        uploading={uploading}
        onChangePhoto={() => setCropping(true)}
        onRemovePhoto={onRemoveAvatar}
      />

      {/* Account info FIRST — read-only confirmation of "this is me" */}
      <AccountInfoCard me={me} />

      {/* Sign out — surfaced HIGH (right after identity) so a broker can
          log out without scrolling past every edit card. Opens a confirm
          modal (rendered below), so an accidental tap is harmless. */}
      <button
        onClick={() => setSignOutOpen(true)}
        className="w-full inline-flex items-center justify-center gap-2 rounded-2xl
                   bg-rose-50 border border-rose-200 text-rose-700
                   hover:bg-rose-100
                   dark:bg-rose-500/10 dark:border-rose-400/30 dark:text-rose-300
                   dark:hover:bg-rose-500/20
                   py-3 font-semibold transition">
        <Icon name="logout" size={18} /> {t('agent.profile.signOut.cta')}
      </button>

      {/* Personal details — editable */}
      <PersonalDetailsCard
        me={me}
        fieldLock={fieldLock}
        isAgent={isAgent}
        onSaved={async () => { await refresh(); flash('ok', t('agent.account.toast.saved')); }}
        onError={(msg) => flash('err', msg)}
      />

      {/* Location — agent-only, summary by default if set */}
      {isAgent && (
        <LocationCard
          onSaved={async () => { flash('ok', t('agent.account.toast.locationSaved')); }}
          onError={(msg) => flash('err', msg)}
        />
      )}

      {/* Security — password change */}
      {fieldLock('password') !== 'none' && (
        <SecurityCard
          locked={fieldLock('password') === 'view'}
          onSaved={async () => {
            flash('ok', t('agent.account.toast.passwordChanged'));
            setTimeout(async () => { await logout(); nav('/login', { replace: true }); }, 1200);
          }}
          onError={(msg) => flash('err', msg)}
        />
      )}

      <AnimatePresence>
        {signOutOpen && (
          <SignOutModal
            busy={signOutBusy}
            onConfirm={confirmSignOut}
            onCancel={() => !signOutBusy && setSignOutOpen(false)}
          />
        )}
      </AnimatePresence>

      {cropping && (
        <AvatarCropper
          title={t('agent.account.avatar.cropTitle')}
          onUpload={uploadCropped}
          onClose={() => setCropping(false)}
        />
      )}
    </div>
  );
}

// ─── Hero card ──────────────────────────────────────────────────

function HeroCard({
  avatarUrl, username, email, isAgent, canEditAvatar, uploading,
  onChangePhoto, onRemovePhoto,
}: {
  avatarUrl: string | null;
  username: string;
  email: string;
  isAgent: boolean;
  canEditAvatar: boolean;
  uploading: boolean;
  onChangePhoto: () => void;
  onRemovePhoto: () => void;
}) {
  const t = useT();
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl p-4
                 bg-gradient-to-br from-brand-500 to-brand-700
                 ring-1 ring-brand-300/40
                 shadow-[0_10px_30px_-12px_rgba(var(--brand-rgb),0.55)]
                 text-white"
    >
      <div aria-hidden className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/15 blur-2xl" />
      <div className="relative flex items-center gap-3">
        <div className="relative shrink-0">
          <AdminAvatar url={avatarUrl} username={username} size="lg" ring />
          {canEditAvatar && (
            <button
              onClick={onChangePhoto}
              disabled={uploading}
              className="absolute -bottom-1 -right-1 grid place-items-center w-8 h-8 rounded-full
                         bg-white text-arena-900 hover:bg-arena-100/60
                         shadow-lg disabled:opacity-50 active:scale-95 transition dark:text-white"
              title={t('agent.account.hero.changePhoto')}
            >
              <Icon name={uploading ? 'progress_activity' : 'photo_camera'}
                    size={15} className={uploading ? 'animate-spin' : ''} />
            </button>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-[17px] font-bold truncate leading-tight">{username}</h2>
          <p className="text-[11.5px] opacity-90 truncate">{email}</p>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 rounded-full
                             bg-white/20 ring-1 ring-white/25 px-2 py-0.5
                             text-[9.5px] font-bold uppercase tracking-wider">
              <Icon name={isAgent ? 'support_agent' : 'shield'} size={11} filled />
              {isAgent ? t('agent.account.hero.role.agent') : t('agent.account.hero.role.admin')}
            </span>
            {avatarUrl && canEditAvatar && (
              <button
                onClick={onRemovePhoto}
                disabled={uploading}
                className="text-[10px] font-bold uppercase tracking-wider underline-offset-2
                           opacity-85 hover:opacity-100 hover:underline disabled:opacity-50"
              >
                {t('agent.account.hero.removePhoto')}
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.section>
  );
}

// ─── Account info (read-only) ───────────────────────────────────

function AccountInfoCard({ me }: { me: any }) {
  const t = useT();
  const dash = t('agent.account.info.emptyDash');
  const items: Array<{ k: string; v: string; mono?: boolean }> = [
    { k: t('agent.account.info.memberSince'),
      v: me.created_at ? new Date(me.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : dash },
    { k: t('agent.account.info.lastLogin'),
      v: me.last_login_at ? new Date(me.last_login_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : t('agent.account.info.firstLogin') },
    { k: t('agent.account.info.lastLoginIp'), v: me.last_login_ip || dash },
  ];
  if (me.role === 'agent' && me.parent_admin_id) {
    items.push({ k: t('agent.account.info.reportsTo'), v: t('agent.account.info.reportsToValue', { id: me.parent_admin_id }) });
  }
  if (me.uaid) {
    items.push({ k: t('agent.account.info.agentId'), v: me.uaid, mono: true });
  }

  return (
    <CardShell icon="info" title={t('agent.account.info.title')} subtitle={t('agent.account.info.subtitle')}>
      <dl className="divide-y divide-arena-200/50 dark:divide-arena-600/60">
        {items.map(({ k, v, mono }) => (
          <div key={k} className="flex items-center justify-between gap-2 py-2 first:pt-0 last:pb-0">
            <dt className="text-[11.5px] text-arena-600 dark:text-neon-200/80 shrink-0">{k}</dt>
            <dd className={`text-[12.5px] font-semibold text-arena-900 dark:text-white truncate ${mono ? 'font-mono tracking-wider' : ''}`}>
              {v}
            </dd>
          </div>
        ))}
      </dl>
    </CardShell>
  );
}

// ─── Personal details card ──────────────────────────────────────

function PersonalDetailsCard({
  me, fieldLock, isAgent, onSaved, onError,
}: {
  me: any;
  fieldLock: (f: 'username'|'email'|'avatar'|'password') => 'none'|'view'|'edit';
  isAgent: boolean;
  onSaved: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const t = useT();
  const [username, setUsername] = useState<string>(me.username ?? '');
  const [email,    setEmail]    = useState<string>(me.email ?? '');
  const [phone,    setPhone]    = useState<string>(me.phone ?? '');
  // 2026-05-18 — Identity hardening. Display name fields: first/last
  // (peer-visible) + birthdate (agent-only, private). Backend Zod
  // validates min(2) chars on first/last, YYYY-MM-DD on birthdate;
  // the DB CHECK constraint rejects empty/whitespace too.
  const [firstName, setFirstName] = useState<string>(me.first_name ?? '');
  const [lastName,  setLastName]  = useState<string>(me.last_name  ?? '');
  const [birthdate, setBirthdate] = useState<string>(me.birthdate  ?? '');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setUsername(me.username ?? ''); }, [me.username]);
  useEffect(() => { setEmail(me.email ?? ''); }, [me.email]);
  useEffect(() => { setPhone(me.phone ?? ''); }, [me.phone]);
  useEffect(() => { setFirstName(me.first_name ?? ''); }, [me.first_name]);
  useEffect(() => { setLastName(me.last_name ?? ''); }, [me.last_name]);
  useEffect(() => { setBirthdate(me.birthdate ?? ''); }, [me.birthdate]);

  const uLocked = fieldLock('username') !== 'edit';
  const eLocked = fieldLock('email')    !== 'edit';
  const allLocked = uLocked && eLocked;
  const dirty =
    username !== (me.username ?? '') ||
    email !== (me.email ?? '') ||
    (isAgent && phone !== (me.phone ?? '')) ||
    firstName !== (me.first_name ?? '') ||
    lastName  !== (me.last_name  ?? '') ||
    (isAgent && birthdate !== (me.birthdate ?? ''));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!dirty || busy) return;
    // Client-side guards mirror backend schema + DB CHECK.
    if (firstName && firstName.trim().length < 2) {
      onError(t('agent.account.personal.error.firstNameShort')); return;
    }
    if (lastName && lastName.trim().length < 2) {
      onError(t('agent.account.personal.error.lastNameShort')); return;
    }
    if (isAgent && birthdate && !/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) {
      onError(t('agent.account.personal.error.dobFormat')); return;
    }
    setBusy(true);
    try {
      const body: Record<string, string> = {};
      if (!uLocked && username !== (me.username ?? '')) body.username = username.trim();
      if (!eLocked && email    !== (me.email ?? ''))    body.email    = email.trim();
      if (isAgent  && phone    !== (me.phone ?? ''))    body.phone    = phone.trim();
      if (firstName !== (me.first_name ?? ''))         body.first_name = firstName.trim();
      if (lastName  !== (me.last_name  ?? ''))         body.last_name  = lastName.trim();
      if (isAgent && birthdate !== (me.birthdate ?? '')) body.birthdate = birthdate;
      await adminApi.put('/admin/auth/me', body);
      await onSaved();
    } catch (err) {
      onError((err as ApiError).message || t('agent.account.error.saveFailed'));
    } finally { setBusy(false); }
  }

  return (
    <CardShell
      icon="badge"
      title={t('agent.account.personal.title')}
      subtitle={allLocked
        ? t('agent.account.personal.subtitle.locked')
        : isAgent
          ? t('agent.account.personal.subtitle.agent')
          : t('agent.account.personal.subtitle.admin')}
    >
      <form onSubmit={onSubmit} className="space-y-3">
        {/* 2026-05-18 — Display name + birthdate fields. The first/last
            name pair is what your downline / upline sees in place of
            your username. Birthdate is private (agent-only, never
            shown to peers). Both validate to 2-char min server-side. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldInput label={t('agent.account.personal.field.firstName')} icon="person" value={firstName} onChange={setFirstName} hint={t('agent.account.personal.field.name.hint')} />
          <FieldInput label={t('agent.account.personal.field.lastName')}  icon="person" value={lastName}  onChange={setLastName}  hint={t('agent.account.personal.field.name.hint')} />
        </div>
        {isAgent && (
          <FieldInput label={t('agent.account.personal.field.dob')} icon="cake" type="date" value={birthdate} onChange={setBirthdate} hint={t('agent.account.personal.field.dob.hint')} />
        )}
        <FieldInput label={t('agent.account.personal.field.username')} icon="person" value={username} onChange={setUsername} disabled={uLocked} hint={t('agent.account.personal.field.username.hint')} />
        <FieldInput label={t('agent.account.personal.field.email')} icon="mail" type="email" value={email} onChange={setEmail} disabled={eLocked} />
        {isAgent && (
          <FieldInput
            label={t('agent.account.personal.field.phone')} icon="call" type="tel"
            value={phone} onChange={setPhone}
            placeholder={t('agent.account.personal.field.phone.placeholder')}
            hint={t('agent.account.personal.field.phone.hint')}
          />
        )}
        {!allLocked && (
          <button
            type="submit"
            disabled={!dirty || busy}
            className="w-full inline-flex items-center justify-center gap-1.5
                       px-4 py-2.5 rounded-full
                       bg-brand-500 hover:bg-brand-400 active:bg-brand-600
                       shadow-[0_2px_10px_rgba(var(--brand-rgb),0.4)]
                       text-white text-[13px] font-bold
                       transition active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
          >
            {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="save" size={14} />}
            {busy ? t('agent.account.personal.cta.saving') : dirty ? t('agent.account.personal.cta.save') : t('agent.account.personal.cta.noChanges')}
          </button>
        )}
      </form>
    </CardShell>
  );
}

// ─── Location card (agent-only) ─────────────────────────────────
//
// Refactor (2026-05-12): the outer Edit/summary toggle and the
// fetch-based name resolution that used to live here have been
// removed. LocationCapture now owns the saved-summary + Edit-button
// pattern internally (showSavedSummary defaults to true). We just
// mount it in `mode='self'` and keep the outer Save button that
// PATCHes to the backend — the component handles surfacing the
// summary vs. editor states itself.

function LocationCard({
  onSaved, onError,
}: {
  onSaved: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const t = useT();
  const { admin, refresh } = useAdminAuth();
  const me = admin as any;

  // Phase R6 (2026-05-13): forward pincode_id + pincode (string) from
  // the backend record so the LocationCapture saved-summary card shows
  // "Pin code: NNNNNN" without an extra round-trip. Migration 069
  // added the column on `agents`; the JOIN in agent.js returns the
  // 6-digit string aliased to `pincode`.
  const initial: GeoBag = {
    latitude:    me?.latitude    != null ? Number(me.latitude)  : null,
    longitude:   me?.longitude   != null ? Number(me.longitude) : null,
    state_id:    me?.state_id    ?? null,
    district_id: me?.district_id ?? null,
    area_id:     me?.area_id     ?? null,
    pincode_id:  me?.pincode_id  ?? null,
    pincode:     me?.pincode     ?? null,
    nation:      me?.nation || 'India',
  };
  const [geo, setGeo] = useState<GeoBag>(initial);
  const [busy, setBusy] = useState(false);

  // Re-hydrate the local GeoBag whenever the auth-admin record
  // changes (e.g. after refresh() following a save). Without this
  // the form would keep showing stale values if the user opens the
  // panel after a location update via another path.
  useEffect(() => {
    setGeo({
      latitude:    me?.latitude    != null ? Number(me.latitude)  : null,
      longitude:   me?.longitude   != null ? Number(me.longitude) : null,
      state_id:    me?.state_id    ?? null,
      district_id: me?.district_id ?? null,
      area_id:     me?.area_id     ?? null,
      pincode_id:  me?.pincode_id  ?? null,
      pincode:     me?.pincode     ?? null,
      nation:      me?.nation || 'India',
    });
  }, [me?.latitude, me?.longitude, me?.state_id, me?.district_id, me?.area_id, me?.pincode_id, me?.pincode, me?.nation]);

  const dirty =
    (initial.latitude    ?? null) !== (geo.latitude    ?? null) ||
    (initial.longitude   ?? null) !== (geo.longitude   ?? null) ||
    (initial.state_id    ?? null) !== (geo.state_id    ?? null) ||
    (initial.district_id ?? null) !== (geo.district_id ?? null) ||
    (initial.area_id     ?? null) !== (geo.area_id     ?? null) ||
    (initial.pincode_id  ?? null) !== (geo.pincode_id  ?? null) ||
    (initial.nation      ?? 'India') !== (geo.nation   ?? 'India');

  // Phase R6 (2026-05-13): self-mode save requires GPS lat/lng to
  // be set. Otherwise the next pageview would immediately fire the
  // GeoForcedModal asking for GPS — a useless double-step. Disable
  // the Save button until the user has locked their GPS through
  // the LocationCapture editor.
  const gpsLocked = isGeoLocated(geo);  // L-6: GPS OR area OR pincode is enough
  const canSave = dirty && gpsLocked && !busy;

  async function save() {
    const patch: Record<string, unknown> = {};
    const keys: (keyof GeoBag)[] = ['latitude','longitude','state_id','district_id','area_id','pincode_id','nation'];
    for (const k of keys) {
      const before = (initial[k] ?? null) as unknown;
      const now    = (geo[k]     ?? null) as unknown;
      if (before !== now) patch[k] = geo[k];
    }
    if (Object.keys(patch).length === 0) return;
    setBusy(true);
    try {
      await adminApi.patch('/admin/me-agent/location', patch);
      await refresh();
      await onSaved();
    } catch (e) { onError((e as ApiError).message); }
    finally { setBusy(false); }
  }

  return (
    <CardShell
      icon="my_location"
      title={t('agent.account.location.title')}
      subtitle={t('agent.account.location.subtitle.edit')}
      right={
        <button
          onClick={save}
          disabled={!canSave}
          title={!gpsLocked ? t('agent.geo.hint.disabled') : undefined}
          className="px-3 py-1.5 rounded-full text-[11.5px] font-bold
                     bg-brand-500 hover:bg-brand-400 active:bg-brand-600 text-white
                     shadow-[0_2px_8px_rgba(var(--brand-rgb),0.35)]
                     inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none transition active:scale-95"
        >
          {busy ? <Icon name="progress_activity" size={12} className="animate-spin" /> : <Icon name="save" size={12} />}
          {t('agent.account.location.cta.save')}
        </button>
      }
    >
      <LocationCapture
        value={geo}
        onChange={setGeo}
        mode="self"                          // agent setting OWN location
        disabled={busy}
        inputClassName={INPUT_CLS}
      />
      {/* Phase R6 (2026-05-13): explicit hint when GPS isn't locked.
          Self-mode save is gated on lat/lng being present so we
          don't trip the GeoForcedModal immediately after save —
          tell the agent why the Save button is disabled. */}
      {dirty && !gpsLocked && (
        <p className="mt-2 text-[11.5px] text-amber-700 dark:text-amber-300">
          <Icon name="my_location" size={12} className="inline-block align-text-bottom mr-1" />
          {t('agent.geo.hint.disabled')}
        </p>
      )}
    </CardShell>
  );
}

// ─── Security card ─────────────────────────────────────────────

function SecurityCard({
  locked, onSaved, onError,
}: {
  locked: boolean;
  onSaved: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const t = useT();
  const [curPw, setCurPw]   = useState('');
  const [newPw, setNewPw]   = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (newPw !== newPw2) { onError(t('agent.account.security.error.mismatch')); return; }
    if (newPw.length < 8) { onError(t('agent.account.security.error.tooShort')); return; }
    if (!/[A-Za-z]/.test(newPw) || !/\d/.test(newPw)) {
      onError(t('agent.account.security.error.weak')); return;
    }
    setBusy(true);
    try {
      await adminApi.post('/admin/auth/change-password', {
        currentPassword: curPw, newPassword: newPw,
      });
      setCurPw(''); setNewPw(''); setNewPw2('');
      await onSaved();
    } catch (err) { onError((err as ApiError).message || t('agent.account.error.changeFailed')); }
    finally { setBusy(false); }
  }

  return (
    <CardShell
      icon="lock"
      title={t('agent.account.security.title')}
      subtitle={locked ? t('agent.account.security.subtitle.locked') : t('agent.account.security.subtitle.unlocked')}
    >
      <form onSubmit={onSubmit} className="space-y-3">
        <FieldInput label={t('agent.account.security.field.current')} icon="lock" type="password" value={curPw} onChange={setCurPw} disabled={locked} />
        <FieldInput label={t('agent.account.security.field.new')} icon="key" type="password" value={newPw} onChange={setNewPw} disabled={locked}
                    hint={t('agent.account.security.field.new.hint')} />
        <FieldInput label={t('agent.account.security.field.confirm')} icon="key" type="password" value={newPw2} onChange={setNewPw2} disabled={locked} />
        {!locked && (
          <button
            type="submit"
            disabled={busy || !curPw || !newPw}
            className="w-full inline-flex items-center justify-center gap-1.5
                       px-4 py-2.5 rounded-full
                       bg-rose-600 hover:bg-rose-500 active:bg-rose-700
                       text-white text-[13px] font-bold
                       shadow-[0_2px_10px_rgba(244,63,94,0.35)]
                       transition active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
          >
            {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="lock_reset" size={14} />}
            {busy ? t('agent.account.security.cta.updating') : t('agent.account.security.cta.update')}
          </button>
        )}
      </form>
    </CardShell>
  );
}

// ─── Sign-out confirmation modal ───────────────────────────────
//
// Mirrors the player-end LogoutButton modal exactly so the
// experience is consistent across the two SPAs:
//   - bottom-sheet on phones (slides up from the bottom edge),
//     centred dialog on sm+ — created via `items-end sm:items-center`
//   - simple white/dark card with the platform's `.card` look
//     (rounded-2xl, shadow-xl, soft backdrop-blur)
//   - plain large h3 title, neutral body text, 2-col button grid
//   - rose primary CTA, neutral "stay" ghost
//   - ESC key + click-outside both close (unless busy)
//   - body scroll locked while open

function SignOutModal({
  busy, onConfirm, onCancel,
}: {
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [busy, onCancel]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: [0.2, 0, 0, 1] }}
      className="fixed inset-0 z-[8000] flex items-end sm:items-start sm:items-center justify-center
                 bg-black/60 p-3 overflow-y-auto"
      onClick={busy ? undefined : onCancel}
      role="dialog" aria-modal="true"
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.97 }}
        transition={{ type: 'spring', stiffness: 340, damping: 28, mass: 0.9 }}
        className="w-full max-w-md rounded-2xl backdrop-blur p-5 sm:p-6
                   bg-white dark:bg-arena-700/40
                   border border-arena-200/60 dark:border-arena-600/50
                   shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-arena-900 dark:text-white">
          {t('agent.profile.signOut.modal.title')}
        </h3>
        <p className="mt-2 text-sm text-arena-600 dark:text-neon-200/75">
          {t('agent.profile.signOut.modal.body')}
        </p>
        <div className="mt-5 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3
                       text-base font-semibold transition active:scale-[.98] disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]
                       bg-arena-100 text-arena-900 hover:bg-arena-200
                       dark:bg-arena-700/60 dark:text-neutral-100 dark:hover:bg-arena-600/60
                       border border-arena-200 dark:border-arena-500/40"
          >
            {t('agent.profile.signOut.modal.stay')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-xl px-4 py-2.5 font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
          >
            {busy ? t('agent.profile.signOut.modal.busy') : t('agent.profile.signOut.modal.confirm')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Shared shells ─────────────────────────────────────────────

function CardShell({
  icon, title, subtitle, right, children,
}: {
  icon: string;
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-2xl p-5
                 bg-white dark:bg-arena-700/40
                 border border-arena-200/60 dark:border-arena-600/50
                 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <h3 className="text-[14px] font-semibold text-arena-900 dark:text-white inline-flex items-center gap-2">
            <Icon name={icon} size={16} className="text-brand-600 dark:text-brand-300" />
            {title}
          </h3>
          {subtitle && <p className="text-[11.5px] text-arena-600 dark:text-neon-200/80 mt-0.5">{subtitle}</p>}
        </div>
        {right && <div className="shrink-0">{right}</div>}
      </div>
      {children}
    </motion.section>
  );
}

function FieldInput({
  label, value, onChange, icon, type = 'text', hint, disabled, placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  icon: string;
  type?: string;
  hint?: string;
  disabled?: boolean;
  placeholder?: string;
}) {
  const t = useT();
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/80 mb-1 font-semibold">
        {label}
        {disabled && (
          <span className="ml-1.5 inline-flex items-center gap-0.5 text-amber-600 dark:text-amber-300 font-normal normal-case lowercase">
            <Icon name="lock" size={10} className="inline -mt-0.5" /> {t('agent.account.personal.fieldLockedNote')}
          </span>
        )}
      </span>
      <span className="relative block">
        <Icon
          name={icon}
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-arena-500 dark:text-neon-200/75 pointer-events-none"
        />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          // 2026-05-18 — Date inputs cap at 12rem (≈192px) on mobile so
          // the fixed-length DD/MM/YYYY field doesn't overwhelm narrow
          // viewports. Other input types stay full-width.
          className={`w-full ${type === 'date' ? 'max-w-[10rem] sm:max-w-[12rem]' : ''} rounded-xl pl-9 pr-3 py-2 text-[14px]
                     disabled:opacity-60 disabled:cursor-not-allowed
                     disabled:bg-arena-50/40 dark:disabled:bg-neutral-900/60
                     bg-white dark:bg-arena-950/60
                     border border-arena-200/60 dark:border-arena-600/60
                     text-arena-900 dark:text-white
                     placeholder-arena-400 dark:placeholder-neon-200/55
                     focus:outline-none focus:ring-2 focus:ring-brand-400/40 focus:border-brand-400/60`}
        />
      </span>
      {hint && <span className="mt-1 block text-[10.5px] text-arena-600 dark:text-neon-200/80">{hint}</span>}
    </label>
  );
}
